export type KeylessRejectionShape = {
  code?: unknown;
  constructorName: string;
  message?: unknown;
  reason?: unknown;
  status?: unknown;
  stringified: string;
  type?: unknown;
  wasClean?: unknown;
};

/**
 * V2-34 (216 events / 25 users): "Object captured as promise rejection with
 * keys: [object has no keys]". The SDK describes a rejected value by its OWN
 * enumerable keys, and the values that reach it here have none — a
 * `CloseEvent`, an `ErrorEvent`, a class instance whose state lives on the
 * prototype. Reading the well-known fields through the prototype is what gives
 * the report a shape the SDK cannot see. Returns null for anything that is not
 * a keyless object: those already report fine.
 */
export function describeKeylessRejection(
  reason: unknown,
): KeylessRejectionShape | null {
  if (reason === null || typeof reason !== 'object') return null;
  if (reason instanceof Error) return null;
  if (Object.keys(reason).length > 0) return null;
  const value = reason as Record<string, unknown>;
  let stringified: string;
  try {
    stringified = String(reason);
  } catch {
    stringified = '<unstringifiable>';
  }
  return {
    code: value.code,
    constructorName: reason.constructor?.name ?? 'unknown',
    // A `BaseApiResponseError` (V2-70: minified to `u`) keeps both behind
    // getters — and they are what names the refused request.
    message: value.message,
    reason: value.reason,
    status: value.status,
    stringified,
    type: value.type,
    wasClean: value.wasClean,
  };
}

/**
 * Runs beside the SDK's own `unhandledrejection` handler, never instead of it:
 * the SDK keeps its event, this adds one that names the source.
 *
 * `wasAnswered` is the same question the SDK's `beforeSend` asks
 * (`answeredRejection.ts`): a rejection the user was already shown as a toast
 * gets no companion either, or the pair would survive as half an issue.
 */
export function installKeylessRejectionReporter(
  capture: (message: string, extra: KeylessRejectionShape) => void,
  target: EventTarget = window,
  wasAnswered: (reason: unknown) => boolean = () => false,
) {
  const listener = (event: Event) => {
    const { reason } = event as PromiseRejectionEvent;
    if (wasAnswered(reason)) return;
    const shape = describeKeylessRejection(reason);
    if (shape) capture('Keyless promise rejection', shape);
  };
  target.addEventListener('unhandledrejection', listener);
  return () => target.removeEventListener('unhandledrejection', listener);
}
